import { defineStore } from 'pinia';

import apiService from '@/services/api.service';
import { Config } from '@/config';
import type {
  AdminPageDto,
  CommunityBrandingFontConfig,
  CommunityDto,
  CustomPageDto,
  Locales,
  UserCommunitiesDto,
} from '@/services/types/api.types';
import { Constants } from '@/constants';
import { useAuthStore } from '@/stores/auth.store';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import vueCookies, { type VueCookies as Cookies } from 'vue-cookies';
import { insertOrUpdate } from '@/helpers/store.helper';
import { askCustomerAccessReference, isCustomerAccessRequired } from '@/helpers/customerAccess.helper';
import { getTranslation } from '@/composables/data-display-utils';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';

// vue-cookies types its default export as the plugin; at runtime it is the instance.
const VueCookies = vueCookies as unknown as Cookies;

interface State {
  communities: Array<CommunityDto>;
  // For a superadmin, the customer communities by id (COCO-3425): whether
  // entering asks for the customer's request (not when the superadmin holds a
  // role of their own there), and the end of the access a reference opened.
  customerAccess: Record<number, { required: boolean; until: string | null }>;
  // The customer community the last_community cookie named, whose access had
  // ended: the admin opened elsewhere, and the layout says so once mounted.
  endedCustomerAccess: number | null;
  isLoading: boolean;
  itemLoadingStates: Set<number>;
}

function normalizeBrandingFont(font: CommunityBrandingFontConfig | null | undefined) {
  if (!font?.source || !font?.family) {
    return null;
  }

  if (font.source === 'builtin' && !font.builtin_key) {
    return null;
  }

  if (font.source === 'remote-css' && !font.css_url) {
    return null;
  }

  return font;
}

function buildPreviewURL(slug: string | null | undefined) {
  return Config.web.v1.replace('%COMMUNITY_SLUG%', slug ?? '');
}

function normalizeCommunityBranding(branding: CommunityDto['options']['branding'] | null | undefined) {
  const mode = branding?.mode ?? 'default';
  const preset_key = branding?.preset_key;

  return {
    mode,
    preset_key,
    tokens: {
      colors: mode === 'custom' ? (branding?.tokens?.colors ?? {}) : {},
      typography: mode === 'custom' ? (branding?.tokens?.typography ?? {}) : {},
    },
    fonts: {
      heading: mode === 'custom' ? normalizeBrandingFont(branding?.fonts?.heading ?? null) : null,
      body: mode === 'custom' ? normalizeBrandingFont(branding?.fonts?.body ?? null) : null,
    },
  };
}

function getCustomPageTypes(): any[] {
  const { t } = useI18n();
  return [
    { label: t('custom_page.type.about'), value: Constants.CustomPage.Type.About },
    { label: t('custom_page.type.content'), value: Constants.CustomPage.Type.Content },
    { label: t('custom_page.type.link'), value: Constants.CustomPage.Type.Link },
  ];
}

export const useCommunityStore = defineStore({
  id: 'community',
  state: (): State => ({
    // initialize state from local storage to enable user to stay logged in
    communities: [],
    customerAccess: {},
    endedCustomerAccess: null,
    isLoading: false,
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    community: (state): CommunityDto | undefined => {
      const authStore = useAuthStore();
      return state.communities?.find((val) => val.id === authStore.currentCommunity);
    },
    getItem: (state) => (communityId: number) => state.communities.find((s) => s.id === communityId),
    isCustomer: (state) => (communityId: number) => communityId in state.customerAccess,
    customerAccessUntil: (state) => (communityId: number) => state.customerAccess[communityId]?.until ?? null,
    // The API decides; this lets the admin ask for the reference before trying.
    needsCustomerRequest: (state) => (communityId: number) => {
      const access = state.customerAccess[communityId];
      if (!access?.required) return false;
      return !access.until || new Date(access.until) <= new Date();
    },
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),
    getCustomPageTypes: () => getCustomPageTypes,
    previewURL: (state) => (communityId: number) => {
      const community = state.communities.find((s) => s.id === communityId);
      return buildPreviewURL(community?.slug);
    },
    // For callers that already hold the slug (e.g. a list fed by its own endpoint) and so
    // must not depend on the community being hydrated in this store.
    previewURLForSlug: () => (slug: string | null | undefined) => buildPreviewURL(slug),
  },
  actions: {
    async init() {
      console.debug('init community store');
    },
    async fetchCommunityById(id: number) {
      this.itemLoadingStates.add(id);
      try {
        const community = await apiService.get(`${Config.api.v2}/user/communities/${id}`);
        // Check if the community already exists in the store
        const index = this.communities.findIndex((c) => c.id === id);
        if (index !== -1) {
          // Update the existing community
          this.communities[index] = community.data;
        } else {
          // Add the community to the store if it doesn't exist
          this.communities.push(community.data);
        }
        await this.fetchCustomPages(id);
        const authStore = useAuthStore();
        if (authStore.role === Constants.User.Role.ADMIN) {
          await this.fetchSummary(id);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(id);
      }
    },
    // The communities the user administers, and which of them are customers'.
    async fetchAdminCommunities() {
      const response = await apiService.get<UserCommunitiesDto>(`${Config.api.v2}/user/communities`);
      const adminCommmunities = response.data.communities.filter((c) =>
        [Constants.User.Role.ADMIN, Constants.User.Role.SUPERADMIN, Constants.User.Role.EDITOR].includes(c.role),
      );
      this.customerAccess = Object.fromEntries(
        adminCommmunities
          .filter((c) => c.is_customer)
          .map((c) => [
            c.object_id,
            { required: !!c.customer_access_required, until: c.customer_access_until ?? null },
          ]),
      );
      return adminCommmunities;
    },
    async fetchCommunities(switchCommunity: boolean = true, force: boolean = false) {
      this.isLoading = true;
      try {
        const adminCommmunities = await this.fetchAdminCommunities();
        for (const community of adminCommmunities) {
          if (force) {
            await this.fetchCommunityById(community.object_id);
          } else {
            insertOrUpdate(
              this.communities,
              {
                id: community.object_id,
                title: community.object_title,
                language_enabled: community.language_enabled,
              },
              'id',
            );
          }
        }

        if (switchCommunity) {
          await this.switchCommunity();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    // Resolves false when the superadmin gave up entering a customer community.
    async switchCommunity(community: { id: number } | null = null, forceReload: boolean = false): Promise<boolean> {
      let targetCommunityId;
      const authStore = useAuthStore();
      const fromCookie = !!VueCookies.get('last_community') && !community;
      if (fromCookie) {
        targetCommunityId = parseInt(VueCookies.get('last_community'));
      } else if (community) {
        targetCommunityId = community.id;
      } else {
        targetCommunityId = authStore.currentCommunity ?? this.communities[0]?.id;
      }
      // Nobody chose this community now, so it never takes a superadmin into a
      // customer's without its request: the admin opens elsewhere instead.
      if (!community && this.needsCustomerRequest(targetCommunityId)) {
        if (fromCookie) this.endedCustomerAccess = targetCommunityId;
        targetCommunityId = this.communities.find((c) => !this.needsCustomerRequest(c.id))?.id;
        if (!targetCommunityId) return false;
      }
      if (!(await this.enterCommunity(targetCommunityId, forceReload))) return false;
      VueCookies.set('last_community', targetCommunityId);
      if (forceReload) {
        window.location.href = '/';
        return true;
      }
      await this.fetchCommunityById(targetCommunityId);
      this.setCommunitySlug(this.communities.find((x) => x.id === targetCommunityId)?.slug);
      return true;
    },
    // Gets the token for a community, asking a superadmin for the customer's
    // request first when it is a customer's. The API has the last word: a
    // community the list does not know as a customer's yet (one just created)
    // or an access that just ran out comes back refused, and is asked for then.
    async enterCommunity(communityId: number, forceReload: boolean): Promise<boolean> {
      const authStore = useAuthStore();
      let accessReference: string | null | undefined;
      if (this.needsCustomerRequest(communityId)) {
        accessReference = await askCustomerAccessReference(this.titleOf(communityId));
        if (!accessReference) return false;
      }
      try {
        await authStore.refreshToken(communityId, forceReload, accessReference ?? undefined);
      } catch (error) {
        if (accessReference || !isCustomerAccessRequired(error)) throw error;
        accessReference = await askCustomerAccessReference(this.titleOf(communityId));
        if (!accessReference) return false;
        await authStore.refreshToken(communityId, forceReload, accessReference);
      }
      // The reference opened an access: read until when, for the menu and the header.
      if (accessReference && !forceReload) await this.fetchAdminCommunities();
      return true;
    },
    titleOf(communityId: number): string {
      const community = this.communities.find((c) => c.id === communityId);
      return community ? getTranslation(community, 'title', community.language_enabled) : String(communityId);
    },
    setLastCommunity(communityId: number) {
      VueCookies.set('last_community', communityId);
    },
    clearCommunitySlug(): void {
      window.localStorage.removeItem('communitySlug');
    },
    getCommunitySlug(): string | null {
      return window.localStorage.getItem('communitySlug');
    },
    setCommunitySlug(communitySlug?: string | null): void {
      if (communitySlug) window.localStorage.setItem('communitySlug', communitySlug);
    },
    createCommunity(options: { defaultLanguage: Locales; languageEnabled: Locales[] }): CommunityDto {
      return {
        id: null,
        slug: '',
        status: 2,
        favicon_file: null,
        title: { en: '', fr: '' },
        description: { en: '', fr: '' },
        language_enabled: options.languageEnabled,
        default_language: options.defaultLanguage,
        default_project_theme_key: 'default',
        default_page_theme_key: 'default',
      };
    },
    async saveCommunity(community: CommunityDto): Promise<boolean> {
      try {
        let communityId = community.id;
        community.options = {
          ...community.options,
          branding: normalizeCommunityBranding(community.options?.branding),
        };
        if (communityId) {
          delete community.summary; // TODO (COCO-874): Removed when we will use the new pattern
          await apiService.put(`${Config.api.v2}/community`, community);
        } else {
          // TODO move initialization of options to the backend
          // and find a way to add new options to existing communities
          community.options = {
            export: { pdf: false, ai_analysis: false },
            google_tag_manager: {
              enabled: false,
              container_id: null,
            },
            consultation: {
              map: false,
              citizen_proposal: true,
              moderation_comment: false,
              guided_consultation: false,
            },
            social_networks: {
              facebook: {
                enabled: false,
                url: '',
              },
              instagram: {
                enabled: false,
                url: '',
              },
              linkedin: {
                enabled: false,
                url: '',
              },
              x: {
                enabled: false,
                url: '',
              },
            },
            featured_project_id: null,
            allow_indexing: true,
            loi96: false,
            use_demographics: true,
            pages: { homepage_id: null },
            branding: normalizeCommunityBranding(null),
          };
          const communityCreated = await apiService.post<{ id: number }>(`${Config.api.v2}/communities`, community);
          communityId = communityCreated.data.id;
          // A superadmin who gives up entering the new customer community stays
          // where they were; the community exists all the same.
          if (!(await this.switchCommunity({ id: communityId }, true))) return true;
        }
        await this.fetchCommunityById(communityId);
        // await this.fetchCommunities(false)
        router.push({ name: 'community_details' });
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    /**
     * Duplicates an existing community as the starting point for a new one.
     *
     * Only the new name and account type are sent: the duplicate inherits its languages, themes,
     * branding and options from the source, and the API rejects anything else.
     * Nothing the source community's participants produced is copied.
     */
    async duplicateCommunity(
      source: CommunityDto,
      values: Pick<CommunityDto, 'account_type' | 'title' | 'description'>,
    ): Promise<boolean> {
      let communityCreated;
      try {
        communityCreated = await apiService.post<{ id: number }>(`${Config.api.v2}/communities`, {
          account_type: values.account_type,
          clone_from_id: source.id,
          title: values.title,
          description: values.description,
        });
      } catch (error) {
        console.error(error);
        return false;
      }
      // The duplicate exists from here on. Reporting a failed switch as a failed
      // save would invite a retry, and the retry would create a second copy.
      try {
        await this.switchCommunity({ id: communityCreated.data.id }, true);
      } catch (error) {
        console.error(error);
      }
      return true;
    },
    async deleteCommunity(community: CommunityDto) {
      try {
        await apiService.delete(`${Config.api.v2}/communities/${community.id}`);
        const index = this.communities.findIndex((c) => c.id === community.id);
        this.communities.splice(index, 1);
        await this.fetchCommunities(false);
        const authStore = useAuthStore();
        if (authStore.currentCommunity === community.id) {
          await this.switchCommunity({ id: this.communities[0]?.id }, true);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async saveCommunityImage(communityId: number, type: string, options: SavingOptions) {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/community/${type}`,
        });
        await this.fetchCommunityById(communityId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async removeCommunityImage(communityId: number, type: string) {
      try {
        // type must be banner, favicon, logo, open-graph
        await apiService.delete(`${Config.api.v2}/community/${type}`);
        await this.fetchCommunityById(communityId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async fetchCustomPages(communityId: number) {
      const { data } = await apiService.get<CustomPageDto[]>(`${Config.api.v2}/community/pages`);
      const index = this.communities.findIndex((c) => c.id === communityId);

      // Update the existing community
      this.communities[index].customPages = data;
    },
    async saveCustomPages(customPage: CustomPageDto): Promise<boolean> {
      try {
        console.debug('update custom page', customPage);
        await apiService.put(`${Config.api.v2}/community/pages/${customPage.id}`, customPage);
        // todo: make an error in console...
        this.fetchCustomPages(customPage.community_id);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async addAdmin(communityId: number, email: any) {
      try {
        await apiService.post(`${Config.api.v2}/community/users/:add-as-administrator`, email);
        this.fetchCommunityById(communityId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeAdmin(communityId: number, admin_user: AdminPageDto) {
      try {
        if (!admin_user.User) return false;
        await apiService.post(`${Config.api.v2}/community/users/:remove-administrator`, {
          user_id: admin_user.User.id,
        });
        this.fetchCommunityById(communityId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async fetchCommunityStats(communityId: number, timePeriod: number) {
      const params = {
        id: communityId,
        timePeriod,
      };
      try {
        return await apiService.get(`${Config.api.v2}/community/visitor-metrics`, params);
      } catch (error) {
        console.error(error);
        return { data: [], success: false, error: error instanceof Error ? error.message : 'Unknown error' };
      }
    },
    async fetchSummary(communityId: number) {
      const { data } = await apiService.get<CustomPageDto[]>(`${Config.api.v2}/community/summary`);
      const index = this.communities.findIndex((c) => c.id === communityId);

      // Update the existing community
      this.communities[index].summary = data;
    },
  },
});
